/**
 * Pure date/time helpers shared by the datetime form controls.
 *
 * Two string shapes travel through these helpers:
 *
 *  - **wall clock** — `"YYYY-MM-DDTHH:mm"`. What the form fields hold. It has no
 *    zone of its own; it is read as a clock reading *in* some IANA zone.
 *  - **instant** — what the API stores. Serialised as a naive UTC string
 *    (`"2026-10-14T18:30:00"`) or a real ISO string (`"…Z"`). A naive string
 *    fed to `new Date()` is read as the browser's wall clock, which silently
 *    shifts every value by the viewer's offset, so it is pinned to UTC here.
 *
 * Keeping the conversion in one place is what stops a 2:30 PM value reopening
 * as 6:30 PM.
 */

/** Minutes are picked from a quarter-hour grid everywhere in the app. */
export const MINUTE_STEP = 15;

export const HOUR_OPTIONS = [
  "12",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
];

export const MINUTE_OPTIONS = ["00", "15", "30", "45"];

const DEFAULT_TIME = { hour: "09", minute: "00", meridiem: "AM" };

const pad = (n) => String(n).padStart(2, "0");

/** The zone the browser is in, e.g. `"America/New_York"`. */
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
};

/** Short name for a zone at a given instant, e.g. `"EDT"`. */
export const zoneLabel = (timeZone, at = new Date()) => {
  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
        .formatToParts(at)
        .find((part) => part.type === "timeZoneName")?.value || timeZone
    );
  } catch {
    return timeZone;
  }
};

/** `"YYYY-MM-DDTHH:mm"` built from a Date's **UTC** parts. */
const wallClockFromUtcParts = (date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;

/** How far `timeZone` sits from UTC at that instant, DST included. */
const zoneOffsetMs = (instant, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(instant)
      .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
      Number(parts.second),
    );
    return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
  } catch {
    // Unrecognised zone — fall back to this browser's.
    return -instant.getTimezoneOffset() * 60000;
  }
};

/** Reads an API value as a real instant, treating naive strings as UTC. */
export const parseInstant = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  const naive = /\d{2}:\d{2}/.test(raw) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const date = new Date(naive ? `${raw.replace(" ", "T")}Z` : raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** API instant → wall clock in `timeZone`, ready for the form fields. */
export const instantToWallClock = (value, timeZone) => {
  const instant = parseInstant(value);
  if (!instant) return "";
  return wallClockFromUtcParts(
    new Date(instant.getTime() + zoneOffsetMs(instant, timeZone)),
  );
};

/** Wall clock in `timeZone` → the real instant it names. */
export const wallClockToDate = (value, timeZone) => {
  const [date = "", time = ""] = String(value || "").split("T");
  if (!date || !time) return null;
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  if ([year, month, day, hour, minute].some(Number.isNaN)) return null;
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  // Second pass so a time near a DST switch resolves against the right offset.
  const first = guess - zoneOffsetMs(new Date(guess), timeZone);
  return new Date(guess - zoneOffsetMs(new Date(first), timeZone));
};

/** Wall clock → naive UTC string (`"2026-10-14T18:30:00"`) for the API. */
export const wallClockToNaiveUtc = (value, timeZone) => {
  const utc = wallClockToDate(value, timeZone);
  return utc ? `${wallClockFromUtcParts(utc)}:00` : "";
};

/** Wall clock → full ISO string (`"2026-10-14T18:30:00.000Z"`) for the API. */
export const wallClockToIso = (value, timeZone) => {
  const utc = wallClockToDate(value, timeZone);
  return utc ? utc.toISOString() : "";
};

/** Splits `"2026-10-14T13:15"` into date + 12-hour parts for the selects. */
export const splitDateTime = (value) => {
  const [date = "", time = ""] = String(value || "").split("T");
  if (!date || !time) return { date, ...DEFAULT_TIME };
  const [rawHour, rawMinute] = time.split(":");
  const hour24 = Number(rawHour);
  if (Number.isNaN(hour24)) return { date, ...DEFAULT_TIME };
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  // An older record may sit off the grid; show the nearest listed minute.
  const minute =
    (Math.round((Number(rawMinute) || 0) / MINUTE_STEP) * MINUTE_STEP) % 60;
  return {
    date,
    hour: pad(hour12),
    minute: pad(minute),
    meridiem: hour24 >= 12 ? "PM" : "AM",
  };
};

export const joinDateTime = ({ date, hour, minute, meridiem }) => {
  if (!date) return "";
  const hour12 = Number(hour) % 12;
  const hour24 = meridiem === "PM" ? hour12 + 12 : hour12;
  return `${date}T${pad(hour24)}:${minute}`;
};

/** Wall clock shifted by whole minutes, staying in the same zone. */
export const addMinutes = (value, minutes, timeZone) => {
  const utc = wallClockToDate(value, timeZone);
  if (!utc) return "";
  return instantToWallClock(
    new Date(utc.getTime() + minutes * 60000),
    timeZone,
  );
};

/** "Now" in `timeZone`, rounded up onto the quarter-hour grid. */
export const nowWallClock = (timeZone) => {
  const now = new Date();
  const rounded = new Date(
    Math.ceil(now.getTime() / (MINUTE_STEP * 60000)) * (MINUTE_STEP * 60000),
  );
  return instantToWallClock(rounded, timeZone);
};

/** Today's date in `timeZone` as `"YYYY-MM-DD"`. */
export const todayDate = (timeZone) =>
  instantToWallClock(new Date(), timeZone).slice(0, 10);

/** `"YYYY-MM-DD"` shifted by whole days. */
export const addDays = (dateOnly, days) => {
  if (!dateOnly) return "";
  const [year, month, day] = String(dateOnly).split("-").map(Number);
  if ([year, month, day].some(Number.isNaN)) return "";
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return wallClockFromUtcParts(shifted).slice(0, 10);
};

/** `"YYYY-MM-DD"` shifted by whole months, clamped to the month's last day. */
export const addMonths = (dateOnly, months) => {
  if (!dateOnly) return "";
  const [year, month, day] = String(dateOnly).split("-").map(Number);
  if ([year, month, day].some(Number.isNaN)) return "";
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return wallClockFromUtcParts(target).slice(0, 10);
};

/** First day of the month `dateOnly` falls in. */
export const startOfMonth = (dateOnly) =>
  dateOnly ? `${String(dateOnly).slice(0, 7)}-01` : "";

/** Plain-language echo of a wall clock, e.g. `"Fri, Oct 14, 2026, 2:30 PM EDT"`. */
export const describeWallClock = (value, timeZone) => {
  const utc = wallClockToDate(value, timeZone);
  if (!utc) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(utc);
  } catch {
    return utc.toLocaleString();
  }
};

/** Plain-language echo of a `"YYYY-MM-DD"`, e.g. `"Fri, Oct 14, 2026"`. */
export const describeDate = (dateOnly) => {
  if (!dateOnly) return "";
  const [year, month, day] = String(dateOnly).split("-").map(Number);
  if ([year, month, day].some(Number.isNaN)) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

const RELATIVE_UNITS = [
  ["year", 365 * 24 * 60],
  ["month", 30 * 24 * 60],
  ["day", 24 * 60],
  ["hour", 60],
  ["minute", 1],
];

/** `"in 3 days"` / `"2 hours ago"` for a wall clock, or `""` when unset. */
export const describeRelative = (value, timeZone) => {
  const utc = wallClockToDate(value, timeZone);
  if (!utc) return "";
  const diffMinutes = Math.round((utc.getTime() - Date.now()) / 60000);
  if (diffMinutes === 0) return "now";
  try {
    const format = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });
    const magnitude = Math.abs(diffMinutes);
    const [unit, size] =
      RELATIVE_UNITS.find(([, minutes]) => magnitude >= minutes) ||
      RELATIVE_UNITS[RELATIVE_UNITS.length - 1];
    return format.format(Math.round(diffMinutes / size), unit);
  } catch {
    return "";
  }
};

/** Wall-clock strings sort lexicographically, so plain comparison is enough. */
export const isBefore = (a, b) => Boolean(a) && Boolean(b) && a < b;
